import type { Graph } from '@antv/x6';
import { applyFlowAnimation, removeFlowAnimation, pulseNode } from './dataflow';

/**
 * 过程执行模拟：
 * 按拓扑顺序模拟 OPM 过程执行。
 * 每一步：激活过程 → 沿边流动数据 → 完成过程。
 */

export interface ExecutionPlan {
  steps: ExecutionStep[];
}

export interface ExecutionStep {
  type: 'process-activate' | 'edge-flow' | 'node-update' | 'process-complete';
  nodeId?: string;
  edgeId?: string;
  duration: number;
  label?: string;
}

/**
 * 根据图的拓扑结构构建执行计划。
 * 按拓扑顺序查找过程并创建执行步骤。
 */
export function buildExecutionPlan(graph: Graph): ExecutionPlan {
  const nodes = graph.getNodes();
  const edges = graph.getEdges();

  // 查找过程节点（type === 'process'）
  const processNodes = nodes.filter((n) => {
    const data = n.getData() || {};
    return data.type === 'process';
  });

  // 构建邻接表用于拓扑排序
  const inDegree = new Map<string, number>();
  const adjacency = new Map<string, string[]>();

  processNodes.forEach((p) => {
    inDegree.set(p.id, 0);
    adjacency.set(p.id, []);
  });

  // 仅考虑过程节点之间的边
  edges.forEach((e) => {
    const srcId = e.getSourceCellId();
    const tgtId = e.getTargetCellId();
    const srcIsProcess = processNodes.some((p) => p.id === srcId);
    const tgtIsProcess = processNodes.some((p) => p.id === tgtId);
    if (srcIsProcess && tgtIsProcess) {
      inDegree.set(tgtId, (inDegree.get(tgtId) || 0) + 1);
      adjacency.get(srcId)?.push(tgtId);
    }
  });

  // 拓扑排序（Kahn 算法）
  const queue: string[] = [];
  inDegree.forEach((deg, id) => {
    if (deg === 0) queue.push(id);
  });

  const order: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift()!;
    order.push(current);
    for (const neighbor of adjacency.get(current) || []) {
      const newDeg = (inDegree.get(neighbor) || 1) - 1;
      inDegree.set(neighbor, newDeg);
      if (newDeg === 0) queue.push(neighbor);
    }
  }

  // 添加剩余过程（处理存在环的情况）
  processNodes.forEach((p) => {
    if (!order.includes(p.id)) order.push(p.id);
  });

  // 构建步骤
  const steps: ExecutionStep[] = [];

  order.forEach((processId) => {
    const processNode = graph.getCellById(processId);
    const processLabel = processNode?.getData()?.label || processId;

    // 查找入边（消耗/作用）
    const incomingEdges = edges.filter((e) => e.getTargetCellId() === processId);
    // 查找出边（生成/调用）
    const outgoingEdges = edges.filter((e) => e.getSourceCellId() === processId);

    // 步骤 1：激活过程
    steps.push({
      type: 'process-activate',
      nodeId: processId,
      duration: 600,
      label: `执行: ${processLabel}`,
    });

    // 步骤 2：沿入边流动数据
    incomingEdges.forEach((e) => {
      steps.push({
        type: 'edge-flow',
        edgeId: e.id,
        duration: 800,
        label: `数据流入: ${e.getData()?.label || e.id}`,
      });
    });

    // 步骤 3：完成过程
    steps.push({
      type: 'process-complete',
      nodeId: processId,
      duration: 500,
      label: `完成: ${processLabel}`,
    });

    // 步骤 4：沿出边流动数据
    outgoingEdges.forEach((e) => {
      steps.push({
        type: 'edge-flow',
        edgeId: e.id,
        duration: 800,
        label: `数据流出: ${e.getData()?.label || e.id}`,
      });

      // 更新目标节点
      const targetId = e.getTargetCellId();
      if (targetId) {
        steps.push({
          type: 'node-update',
          nodeId: targetId,
          duration: 400,
          label: `更新: ${graph.getCellById(targetId)?.getData()?.label || targetId}`,
        });
      }
    });
  });

  return { steps };
}

/**
 * 逐步执行模拟计划。
 */
export function runExecution(
  graph: Graph,
  plan: ExecutionPlan,
  onStepChange?: (stepIndex: number, step: ExecutionStep) => void,
): { stop: () => void } {
  let cancelled = false;
  let currentStep = 0;

  const executeNext = async () => {
    if (cancelled || currentStep >= plan.steps.length) {
      // 清理所有动画
      cleanupSimulation(graph);
      return;
    }

    const step = plan.steps[currentStep];
    onStepChange?.(currentStep, step);

    switch (step.type) {
      case 'process-activate':
        if (step.nodeId) {
          graph.getCellById(step.nodeId)?.setData({
            active: true,
            executionState: 'running',
          });
        }
        break;

      case 'edge-flow':
        if (step.edgeId) {
          const cell = graph.getCellById(step.edgeId);
          if (cell?.isEdge()) {
            const edge = cell as any;
            applyFlowAnimation(edge);
            // 脉冲源节点
            const srcId = edge.getSourceCellId?.();
            if (srcId) pulseNode(graph, srcId, step.duration);
            // 延迟后脉冲目标节点
            setTimeout(() => {
              const tgtId = edge.getTargetCellId?.();
              if (tgtId) pulseNode(graph, tgtId, step.duration);
            }, step.duration * 0.4);
          }
        }
        break;

      case 'node-update':
        if (step.nodeId) {
          pulseNode(graph, step.nodeId, step.duration);
        }
        break;

      case 'process-complete':
        if (step.nodeId) {
          graph.getCellById(step.nodeId)?.setData({
            active: false,
            executionState: 'completed',
          });
        }
        break;
    }

    currentStep++;
    setTimeout(() => {
      if (!cancelled) executeNext();
    }, step.duration);
  };

  executeNext();

  return {
    stop: () => {
      cancelled = true;
      cleanupSimulation(graph);
    },
  };
}

/**
 * 重置图上所有模拟状态。
 */
export function cleanupSimulation(graph: Graph): void {
  graph.getNodes().forEach((node) => {
    node.setData({
      active: false,
      executionState: 'idle',
    });
  });
  graph.getEdges().forEach((edge) => {
    removeFlowAnimation(edge);
  });
}
